import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, AlertTriangle, ShieldCheck } from 'lucide-react';
import { API, useAuth, formatApiError } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Logo } from '@/components/Logo';
import { ThemeToggle } from '@/components/ThemeToggle';
import { AUTH } from '@/constants/testIds';

export default function ResetPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [checking, setChecking] = useState(true);
  const [linkError, setLinkError] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        await axios.get(`${API}/auth/reset-password/${token}`);
      } catch (err) {
        setLinkError(formatApiError(err?.response?.data?.detail) || 'This reset link is invalid.');
      } finally {
        setChecking(false);
      }
    })();
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/auth/reset-password/${token}`,
        { password },
        { withCredentials: true }
      );
      await refresh();
      navigate('/', { replace: true });
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || 'Could not reset password.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid={AUTH.resetPage} className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-2 bg-white">
      <div
        className="hidden lg:block relative bg-cover bg-center"
        style={{
          backgroundImage:
            'linear-gradient(180deg, rgba(10,37,64,0.72) 0%, rgba(10,37,64,0.88) 100%), url(https://images.unsplash.com/photo-1512403754473-27835f7b9984?auto=format&fit=crop&w=1600&q=80)',
        }}
      >
        <div className="absolute inset-0 flex flex-col justify-between text-white">
          <Logo variant="wordmark" className="w-full h-auto block" alt="STEELPEAK" />
          <div className="p-14 pt-0">
            <h2 className="font-display text-4xl xl:text-5xl leading-tight max-w-md">
              Choose a new password.
            </h2>
            <p className="mt-6 text-sm text-white/70 max-w-sm leading-relaxed">
              You'll be signed in automatically once your new password is saved.
            </p>
          </div>
        </div>
      </div>

      <div className="relative flex items-center justify-center px-6 py-16 lg:px-16 bg-[#FAFAFA]">
        <div className="absolute top-6 right-6"><ThemeToggle /></div>
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-10">
            <Logo variant="wordmark" className="h-7 w-auto rounded" alt="STEELPEAK" />
          </div>

          {checking && (
            <div className="flex items-center gap-3 text-slate-500 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" /> Verifying your reset link…
            </div>
          )}

          {!checking && linkError && (
            <div className="space-y-6">
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-red-200 bg-red-50">
                <AlertTriangle className="h-6 w-6 text-red-600" strokeWidth={1.5} />
              </div>
              <h1 className="font-display text-4xl text-[#0A2540]">Link problem</h1>
              <p data-testid={AUTH.resetError} className="text-sm text-slate-500 leading-relaxed">
                {linkError}
              </p>
              <Link to="/forgot-password">
                <Button className="bg-[#0A2540] hover:bg-slate-800 text-white h-11 rounded-md px-5">
                  Request a new link
                </Button>
              </Link>
            </div>
          )}

          {!checking && !linkError && (
            <>
              <div className="mb-10">
                <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] mb-3">
                  Reset Password
                </div>
                <h1 className="font-display text-4xl md:text-5xl text-[#0A2540] leading-tight">
                  New password
                </h1>
                <p className="mt-3 text-sm text-slate-500">
                  Choose something you haven't used elsewhere.
                </p>
              </div>

              <form onSubmit={onSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-600">
                    New password
                  </Label>
                  <Input
                    type="password"
                    required
                    minLength={6}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid={AUTH.resetPassword}
                    className="h-11 rounded-md border-slate-300 focus-visible:ring-[#0A2540]"
                    placeholder="Minimum 6 characters"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-600">
                    Confirm password
                  </Label>
                  <Input
                    type="password"
                    required
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    data-testid={AUTH.resetConfirm}
                    className="h-11 rounded-md border-slate-300 focus-visible:ring-[#0A2540]"
                    placeholder="Retype password"
                  />
                </div>
                {error && (
                  <div
                    data-testid={AUTH.resetError}
                    className="text-sm text-red-600 border-l-2 border-red-500 pl-3 py-1"
                  >
                    {error}
                  </div>
                )}
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid={AUTH.resetSubmit}
                  className="w-full h-11 bg-[#0A2540] hover:bg-slate-800 text-white rounded-md tracking-wide gap-2"
                >
                  <ShieldCheck className="h-4 w-4" />
                  {submitting ? 'Saving…' : 'Save password'}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
